import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthLogoutAction } from '@client/actions/auth-actions';
import {
	LayoutClickAction,
	LayoutClickAdminMenuAction,
	LayoutClickUserMenuAction,
	LayoutCloseSideNavAction,
	LayoutCollapseAdminOptionsAction
} from '@client/actions/layout-actions';
import {
	getLayoutAdminMenu,
	getLayoutEnableAdminOptions,
	getLayoutIsAdminMenu,
	getLayoutMenu,
	getLayoutScopeId,
	getLayoutScreenMobile,
	getLayoutScreenTablet,
	getLayoutSelectedGroup,
	getLayoutSelectedItem,
	getLayoutShowSidebav,
	getOrganizationById,
	getSessionClaimsAccessLevel,
	getSessionUid,
	getUserSubscriptionSpaqContractNo,
	getUserSubscriptionSpaqEmail
} from '@client/selectors';
import { ElementRef, ViewChild, HostListener } from '@angular/core';
import { getSessionClaimsAdmin } from '@client/selectors/index';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { combineLatest, map, switchMap, withLatestFrom, take } from 'rxjs/operators';

@Component({
	selector: 'spaq-layout-account',
	template: `
		<spaq-layout-account-container class="d-flex flex-row h-100" style="position: absolute; top: 0; bottom: 0; right: 0; left: 0">
			<spaq-layout-account-sidebar
				[isAdmin]="isAdmin$ | async"
				[selectedGroup]="selectedGroup$ | async"
				[selectedItem]="selectedItem$ | async"
				[isAdminMenu]="isAdminMenu$ | async"
				[isStaff]="isStaff$ | async"
				[enableAdminOptions]="enableAdminOptions$ | async"
				[hidden]="hide$ | async"
				(itemClick)="onSidebarClick($event)"
				(clickAdminMenu)="onClickAdminMenu()"
				(clickEnableAdminOptions)="onEnableAdminOptions()"
				(clickUserMenu)="onClickUserMenu()"
				[items]="items$ | async"
				[style.display]="(hide$ | async) ? 'none' : 'block'"
				class="p-0 w-100 side-container"
				style="max-width: 320px"
				#sidebarRef
			></spaq-layout-account-sidebar>

			<spaq-layout-account-main id="accountMain" class="p-0 d-flex w-100 overflow-auto">
				<div *ngIf="isAdminLoggedAsUser" class="alert alert-danger m-0" style="color: red; font-size: 1.2rem;">
					<i class="fa fa-warning pe-2"></i>Vous êtes présentement identifié en tant qu'administrateur sur le compte de
					<b>{{ email$ | async }} au contrat {{ contractNo$ | async }}</b
					><i class="fa fa-warning ps-2"></i>
				</div>
				<div *ngIf="isCustomApplication" class="alert alert-warning m-0" style="color: red; font-size: 1.2rem;">
					<span class="pr-4"
						>Vous êtes identifié sur l'application <b>{{ selectedOrganization }}</b> via {{ realScope }}. </span
					><button class="btn btn-sm btn-secondary mb-0 ml-4" (click)="onRestoreScope()">
						Revenir sur l'application {{ realScope }}
					</button>
				</div>
				<spaq-layout-account-header class="w-100"></spaq-layout-account-header>
				<router-outlet></router-outlet>
			</spaq-layout-account-main>
		</spaq-layout-account-container>
	`,
	styles: [
		`
			.oo {
				overflow: auto;
			}
			@media (max-width: 992px) {
				.side-container {
					max-width: 320px;
					position: fixed;
					z-index: 1000;
					height: 100%;
					top: 0;
					left: 0;
					bottom: 0;
				}
			}

			spaq-layout-account-sidebar {
			}
		`
	]
})
export class LayoutAccountComponent implements OnInit {
	desktop$ = this.store.select(state => state.screen.desktop);
	enableAdminOptions$ = this.store.pipe(select(getLayoutEnableAdminOptions));
	isAdmin$ = this.store.pipe(select(getSessionClaimsAdmin));
	isAdminMenu$ = this.store.pipe(select(getLayoutIsAdminMenu));
	isAdminLoggedAsUser = false;
	isCustomApplication = false;
	selectedOrganization = localStorage.getItem('selectedOrganization');
	realScope = document.location.hostname.split('.')[0].toUpperCase();
	isStaff$ = this.store.pipe(
		select(getSessionClaimsAccessLevel),
		map(x => x === 5)
	);
	items$ = of({});
	mobile$ = this.store.pipe(select(getLayoutScreenMobile));
	tablet$ = this.store.pipe(select(getLayoutScreenTablet));
	hide$: Observable<any> = this.store.select(getLayoutShowSidebav).pipe(
		combineLatest(this.mobile$, this.tablet$),
		map(x => {
			return !x[0];
		})
	);
	showSidenav$ = this.store.pipe(select(getLayoutShowSidebav));
	public opened = true;
	selectedGroup$ = this.store.pipe(select(getLayoutSelectedGroup));
	selectedItem$ = this.store.pipe(select(getLayoutSelectedItem));

	email$ = this.store.pipe(select(getUserSubscriptionSpaqEmail));
	uuid$ = this.store.pipe(select(getSessionUid));
	contractNo$ = this.store.pipe(select(getUserSubscriptionSpaqContractNo));
	scopeId$ = this.store.pipe(select(getLayoutScopeId));
	isSelectableGroup$ = this.scopeId$.pipe(
		switchMap(id =>
			this.store.pipe(select(getOrganizationById, id)).pipe(map((x: any) => Boolean(x && x.config && x.config.enableSelectableGroup)))
		)
	);

	@ViewChild('sidebarRef', { static: false, read: ElementRef }) sidebarRef: ElementRef;
	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const sidebarEl = this.sidebarRef?.nativeElement;
		const target = event.target as HTMLElement;
		const clickedId = target?.id;

		if (!sidebarEl) {
			return;
		}

		const clickedInside = sidebarEl.contains(event.target);
		const _sidebarWidth = sidebarEl.getBoundingClientRect().width; // eslint-disable-line @typescript-eslint/no-unused-vars
		if (!clickedInside && clickedId !== 'collapseMenu') {
			this.mobile$
				.pipe(take(1), withLatestFrom(this.tablet$, this.showSidenav$, this.hide$))
				.subscribe(([isMobile, isTablet, _isOpen, _isHide]) => {
					if (isMobile || isTablet) {
						this.store.dispatch(new LayoutCloseSideNavAction(null, Date.now()));
					}
				})
				.unsubscribe(); // éviter les fuites mémoire
		}
	}
	constructor(private store: Store<any>, private router: Router) {
		if (
			this.selectedOrganization &&
			this.realScope &&
			this.selectedOrganization.length > 2 &&
			this.selectedOrganization !== this.realScope
		) {
			this.isCustomApplication = true;
		}
		this.items$ = this.isAdminMenu$.pipe(
			switchMap(isAdmin =>
				this.store.pipe(
					select((isAdmin && getLayoutAdminMenu) || getLayoutMenu),
					withLatestFrom(this.isSelectableGroup$),
					map(([x, isSelectableGroup]) => {
						if (x) {
							const values = Object.keys(x)
								.filter(x => (isSelectableGroup && x === 'add_access_code' ? false : true))
								.map(key => x[key]);
							return values.map(l => {
								if (!l.children) {
									return l;
								}

								const lvalues = Object.keys(l.children).map(key => l.children[key]);
								return { ...l, children: lvalues };
							});
						}
						return x;
					})
				)
			)
		);
	}
	onRestoreScope() {
		localStorage.removeItem('selectedOrganization');
		window.location.reload();
	}

	ngOnInit() {
		this.uuid$.subscribe(e => {
			this.isAdminLoggedAsUser = localStorage.getItem('isAdminLoggedAsUser') === e;
		});
	}

	delete() {}

	logout() {
		this.store.dispatch(new AuthLogoutAction(null, Date.now()));
	}

	onClickAdminMenu() {
		this.store.dispatch(new LayoutClickAdminMenuAction(null, Date.now()));
	}

	onClickUserMenu() {
		this.store.dispatch(new LayoutClickUserMenuAction(null, Date.now()));
	}

	onEnableAdminOptions() {
		this.store.dispatch(new LayoutCollapseAdminOptionsAction(null, Date.now()));
	}

	onSidebarClick($event) {
		this.store.dispatch(new LayoutClickAction($event, Date.now()));
	}

	retryRefresh() {}
}
